package org.zest.insan_360_website.pages.user.admins_layout

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Input
import org.zest.insan_360_website.components.layouts.*
import org.zest.insan_360_website.components.widgets.*
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AuthState
import org.zest.insan_360_website.data.page_states.UserPreferences
import org.zest.insan_360_website.data.page_states.dashboard.AdminsDashboardState.adminsInfoState
import org.zest.insan_360_website.data.page_states.dashboard.AdminsDashboardState.isLoading
import org.zest.insan_360_website.data.page_states.dashboard.AdminsDashboardState.isVerifying
import org.zest.insan_360_website.data.page_states.dashboard.AdminsDashboardState.needUpdate
import org.zest.insan_360_website.data.page_states.dashboard.AdminsDashboardState.searchQuery
import org.zest.insan_360_website.data.page_states.dashboard.AdminsDashboardState.verifyAdmin
import org.zest.insan_360_website.model.Admin
import org.zest.insan_360_website.model.UserRole
import org.zest.insan_360_website.utils.getSearchScore

val supAdmDashContentLayout = CssStyle {
    base {
        Modifier
            .grid {
                columns { repeat(2) { size(1.fr) } }
                rows { repeat(2) { size(1.fr) } }
            }
    }
}

val supAdmContainerGrid = CssStyle {
    base {
        Modifier
            .gridItem(1, 1, 2, 2)
    }
}

val adminInfoStyle = CssStyle {
    base {
        BaseButtonModifier
            .backgroundColor(userPageColor)
            .borderRadius(0.75.cssRem)
    }
    hover { Modifier.backgroundColor(userPageBGColor.darkened(0.1f)) }
    active { Modifier.backgroundColor(userPageBGColor.darkened(0.2f)) }
}

@OptIn(DelicateApi::class)
@Composable
fun SuperAdminLayout() {
    PageContentBaseCol {
        UnverifiedAdminList()
        AdminList()
    }
}

@OptIn(DelicateApi::class)
@Composable
private fun AdminList() {
    val breakpoint = rememberBreakpoint()
    val modal = LocalModal.current

    TableFlushedListContainer(
        5,
        header = {
            if (breakpoint >= Breakpoint.MD) {
                Row(
                    Modifier
                        .fillMaxWidth()
                        .gap(0.5.cssRem),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        userDashWordLibrary.admin_list.localized,
                        Modifier
                            .fontWeight(FontWeight.Bold)
                            .fontSize(1.5.cssRem)
                            .whiteSpace(WhiteSpace.NoWrap)
                    )
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .justifyContent(JustifyContent.FlexEnd)
                            .gap(0.5.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        AdminFilterInputs()
                    }
                }
            } else {
                Column(
                    Modifier
                        .fillMaxWidth()
                        .gap(0.5.cssRem)
                ) {
                    SpanText(
                        userDashWordLibrary.admin_list.localized,
                        Modifier
                            .fontWeight(FontWeight.Bold)
                            .fontSize(1.5.cssRem)
                            .whiteSpace(WhiteSpace.NoWrap)
                    )
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .justifyContent(JustifyContent.FlexStart)
                            .gap(0.5.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        AdminFilterInputs()
                    }
                }
            }
        },
        listHeaders = {
            ListHeader(
                "ID",
                modifier = Modifier
                    .position(Position.Sticky)
                    .left(0.5.cssRem)
                    .zIndex(2)
                    .backgroundColor(userPageBGColor)
                    .styleModifier { property("box-shadow", "-0.5rem 0 0 0 $userPageBGColor") }
            )
            ListHeader(userDashWordLibrary.name.localized)
            ListHeader(userDashWordLibrary.email.localized)
            ListHeader(userDashWordLibrary.phone_number.localized)
            ListHeader(userDashWordLibrary.organization.localized)
            ListHeader("", true)
        },
        listHeadersModifier = Modifier
            .gap(0.25.cssRem)
            .grid {
                columns {
                    size(2.fr)
                    size(4.fr)
                    size(4.fr)
                    size(2.fr)
                    size(3.fr)
                    size(24.px)
                }
            }
    ) {
        val isLoading = isLoading
        val verifiedAdminBase = adminsInfoState?.admins?.filter { it.isVerified }

        if (isLoading) {
            Box(Modifier.fillMaxWidth().minHeight(150.px).padding(1.cssRem), contentAlignment = Alignment.Center) {
                DotLoading(userPagePrimaryTextColor)
            }
        } else if (verifiedAdminBase.isNullOrEmpty()) {
            Box(Modifier.fillMaxWidth().minHeight(150.px).padding(1.cssRem), contentAlignment = Alignment.Center) {
                SpanText(
                    userDashWordLibrary.no_verified_admin_found.localized,
                    Modifier
                        .color(userPageSecondaryTextColor)
                        .fontWeight(FontWeight.Medium)
                )
            }
        } else {
            val verifiedAdmin = if (searchQuery.isBlank()) {
                verifiedAdminBase
            } else {
                verifiedAdminBase.asSequence().mapNotNull { admin ->
                    val nameScore = if (admin.name.isNotBlank()) getSearchScore(searchQuery, admin.name) else Int.MAX_VALUE
                    val emailScore = if (admin.email.isNotBlank()) getSearchScore(searchQuery, admin.email) else Int.MAX_VALUE
                    val orgScore = if (admin.organizationName.isNotBlank()) getSearchScore(searchQuery, admin.organizationName) else Int.MAX_VALUE
                    
                    val bestScore = minOf(nameScore, emailScore, orgScore)
                    
                    if (bestScore <= 130) admin to bestScore else null
                }.sortedBy { it.second }
                    .map { it.first }
                    .toList()
            }

            if (verifiedAdmin.isEmpty()) {
                Box(Modifier.fillMaxWidth().minHeight(150.px).padding(1.cssRem), contentAlignment = Alignment.Center) {
                    SpanText(
                        (userDashWordLibrary.no_results_for.localized) +
                                " \"${searchQuery}\"",
                        Modifier
                            .color(userPageSecondaryTextColor).fontWeight(FontWeight.Medium)
                    )
                }
            } else {
                verifiedAdmin.forEachIndexed { index, admin ->
                    FlushedListRowWrapper(
                        index = index,
                        modifier = Modifier
                            .cursor(Cursor.Pointer)
                            .onClick {
                                modal.show(Modifier.fillMaxWidth()) {
                                    AdminInfoModal(admin)
                                }
                            }
                    ) {
                        val rowBg = if (index % 2 == 0) userPageColor else userPageColor.darkened(0.05f)
                        Row(
                            Modifier
                                .fillMaxWidth()
                                .minWidth(1200.px)
                                .gap(0.25.cssRem)
                                .grid {
                                    columns {
                                        size(2.fr)
                                        size(4.fr)
                                        size(4.fr)
                                        size(2.fr)
                                        size(3.fr)
                                        size(24.px)
                                    }
                                },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                Modifier
                                    .fillMaxWidth()
                                    .position(Position.Sticky)
                                    .left(0.5.cssRem)
                                    .zIndex(2)
                                    .backgroundColor(rowBg)
                                    .styleModifier { property("box-shadow", "-0.5rem 0 0 0 $rowBg") }
                            ) {
                                CopyableIdText(admin.id)
                            }
                            ListEntry(admin.name)
                            ListEntry(admin.email)
                            ListEntry(admin.phone)
                            ListEntry(admin.organizationName)
                            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                Box(
                                    Modifier.size(1.25.cssRem),
                                    contentAlignment = Alignment.Center
                                ) {
                                    FaChevronRight(Modifier.color(userPageSecondaryTextColor).fontSize(0.8.cssRem))
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun UnverifiedAdminList() {
    val modal = LocalModal.current

    FlushedListContainer(
        2,
        {
            SpanText(
                userDashWordLibrary.admin_verification.localized,
                Modifier.fontWeight(FontWeight.Bold).fontSize(1.5.cssRem)
            )
        }
    ) {
        val isLoading = isLoading
        val unverifiedAdmins = adminsInfoState?.admins?.filter { !it.isVerified }

        if (isLoading) {
            Box(Modifier.fillMaxWidth().minHeight(150.px).padding(1.cssRem), contentAlignment = Alignment.Center) {
                DotLoading(userPagePrimaryTextColor)
            }
        } else if (unverifiedAdmins.isNullOrEmpty()) {
            Box(Modifier.fillMaxWidth().minHeight(150.px).padding(1.cssRem), contentAlignment = Alignment.Center) {
                SpanText(
                    userDashWordLibrary.no_admin_verification_needed.localized,
                    Modifier.color(userPageSecondaryTextColor).fontWeight(FontWeight.Medium)
                )
            }
        } else {
            unverifiedAdmins.forEachIndexed { index, admin ->
                FlushedListRowWrapper(
                    index = index,
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick {
                            modal.show(Modifier.fillMaxWidth()) {
                                AdminInfoModal(admin)
                            }
                        }
                ) {
                    Row(
                        Modifier.fillMaxWidth().gap(0.5.cssRem),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column(Modifier.flex(1).gap(0.25.cssRem)) {
                            SpanText(admin.name, Modifier.fontWeight(FontWeight.SemiBold).fontSize(1.cssRem))
                            SpanText(admin.email, Modifier.color(userPageSecondaryTextColor).fontSize(0.85.cssRem))
                        }
                        Column(Modifier.flex(1), horizontalAlignment = Alignment.End) {
                            SpanText(
                                admin.organizationName,
                                Modifier.fontWeight(FontWeight.Medium).fontSize(0.9.cssRem)
                                    .color(userPageSecondaryTextColor)
                            )
                        }
                        Box(Modifier.padding(left = 0.5.cssRem)) {
                            Box(
                                Modifier
                                    .size(1.75.cssRem)
                                    .borderRadius(50.percent)
                                    .backgroundColor(userPageBGColor)
                                    .border {
                                        width(1.px)
                                        style(LineStyle.Solid)
                                        color(userPageBorderStrongColor)
                                    },
                                contentAlignment = Alignment.Center
                            ) {
                                FaChevronRight(Modifier.color(userPageSecondaryTextColor).fontSize(0.8.cssRem))
                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun AdminInfoModal(admin: Admin) {
    val scope = rememberCoroutineScope()
    val toast = LocalToast.current
    val modal = LocalModal.current

    val adminInfoModalLocalized = LanguageSettings.UserDashboardPage.AdminInfoModal
    Column(
        Modifier
            .fillMaxWidth()
            .padding(1.cssRem)
            .gap(1.5.cssRem)
            .userSelect(UserSelect.None)
    ) {
        Row(
            Modifier.fillMaxWidth().justifyContent(JustifyContent.SpaceBetween),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                Modifier.gap(0.75.cssRem).flexWrap(FlexWrap.WrapReverse),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    admin.id,
                    Modifier
                        .fontWeight(FontWeight.Bold)
                        .color(userPageSecondaryTextColor)
                )
                AdminStatusBadge(admin.isVerified)
            }
        }

        Column(Modifier.fillMaxWidth().minWidth(0.px).gap(0.25.cssRem)) {
            SpanText(
                admin.name,
                Modifier
                    .fontSize(1.8.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .lineHeight(1.2)
            )
            Row(
                Modifier.fillMaxWidth().minWidth(0.px).gap(0.5.cssRem).color(userPageSecondaryTextColor)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        window.navigator.clipboard.writeText(admin.email)
                        toast.show(LanguageSettings.UserDashboardPage.email_copied.localized, ToastType.SUCCESS)
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                FaEnvelope(Modifier.flexShrink(0))
                SpanText(
                    admin.email, 
                    Modifier
                        .fontWeight(FontWeight.Medium)
                        .whiteSpace(WhiteSpace.NoWrap)
                        .overflow(Overflow.Hidden)
                        .textOverflow(TextOverflow.Ellipsis)
                        .title(admin.email)
                )
            }
        }
        Box(
            Modifier
                .fillMaxWidth()
                .height(1.px)
                .backgroundColor(userPageBorderStrongColor)
        )

        Column(Modifier.gap(0.5.cssRem)) {
            SpanText(
                adminInfoModalLocalized.personal_details.localized,
                Modifier.fontWeight(FontWeight.SemiBold).fontSize(1.1.cssRem).color(userPagePrimaryTextColor)
            )
            Column(Modifier.gap(0.75.cssRem).padding(top = 0.25.cssRem)) {
                Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    FaHashtag(Modifier.color(userPageSecondaryTextColor).width(1.2.cssRem))
                    SpanText("ID: ${admin.id}", Modifier.userSelect(UserSelect.Text))
                }
                Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    FaIdCard(Modifier.color(userPageSecondaryTextColor).width(1.2.cssRem))
                    SpanText("NRIC: ${admin.nric}")
                }
                Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    FaPhone(Modifier.color(userPageSecondaryTextColor).width(1.2.cssRem))
                    SpanText(admin.phone)
                }
                Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    FaBriefcase(Modifier.color(userPageSecondaryTextColor).width(1.2.cssRem))
                    SpanText(admin.position)
                }
                Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    FaUserTie(Modifier.color(userPageSecondaryTextColor).width(1.2.cssRem))
                    SpanText(admin.workType)
                }
            }
        }

        Box(
            Modifier
                .fillMaxWidth()
                .height(1.px)
                .backgroundColor(userPageBorderStrongColor)
        )

        Column(Modifier.gap(0.5.cssRem)) {
            SpanText(
                adminInfoModalLocalized.organization_details.localized,
                Modifier.fontWeight(FontWeight.SemiBold).fontSize(1.1.cssRem).color(userPagePrimaryTextColor)
            )
            Column(Modifier.gap(0.75.cssRem).padding(top = 0.25.cssRem)) {
                Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    FaBuilding(Modifier.color(userPageSecondaryTextColor).width(1.2.cssRem))
                    SpanText(admin.organizationName, Modifier.fontWeight(FontWeight.Medium))
                }
                Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    FaHashtag(Modifier.color(userPageSecondaryTextColor).width(1.2.cssRem))
                    SpanText("Org ID: ${admin.organizationId}", Modifier.userSelect(UserSelect.Text))
                }
                Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    FaLocationDot(Modifier.color(userPageSecondaryTextColor).width(1.2.cssRem))
                    SpanText(admin.address)
                }
            }
        }

        if (!admin.isVerified) {
            Row(
                Modifier.fillMaxWidth().justifyContent(JustifyContent.FlexEnd)
            ) {
                val isVerifying = isVerifying
                Box(
                    Modifier
                        .fontSize(1.1.cssRem)
                        .onClick {
                            if (!isVerifying) {
                                verifyAdmin(admin.id, toast, scope) {
                                    needUpdate = true
                                    modal.hide()
                                }
                            }
                        }
                        .then(contentButtonStyle.toModifier())
                        .thenIf(isVerifying, Modifier.opacity(0.7).cursor(Cursor.NotAllowed))
                ) {
                    Row(Modifier.gap(0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                        if (isVerifying) {
                            DotLoading(userPageBGColor)
                        }
                        SpanText(
                            if (isVerifying) {
                                adminInfoModalLocalized.verifying.localized
                            }
                            else {
                                adminInfoModalLocalized.verify_admin.localized
                            },
                            modifier = Modifier.then(contentButtonTextStyle.toModifier())
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun AdminFilterInputs() {
    Input(
        InputType.Text,
        attrs = Modifier
            .flex(1)
            .maxWidth(480.px)
            .fontFamily("FunnelDisplay")
            .fontWeight(FontWeight.SemiBold)
            .padding(0.325.cssRem)
            .borderRadius(0.25.cssRem)
            .border {
                width(1.px)
                style(LineStyle.Solid)
                color(userPageBorderStrongColor)
            }
            .backgroundColor(userPageBGColor)
            .color(userPagePrimaryTextColor)
            .outline(0.px)
            .toAttrs {
                value(searchQuery)
                onInput { searchQuery = it.value }
                attr(
                    "placeholder",
                    userDashWordLibrary.search.localized
                )
            }
    )
}

@Composable
private fun AdminStatusBadge(isVerified: Boolean) {
    val statusColor = if (isVerified) Color.rgb(0x16a34a) else Color.rgb(0xd97706)
    val displayText = LanguageSettings.UserDashboardPage
        .AdminInfoModal.verification_state[isVerified]?.get(UserPreferences.language)
        ?: if (isVerified) "VERIFIED" else "NEED VERIFICATION"

    StatusBadge(
        text = displayText,
        color = statusColor
    )
}
